import { Badge, Col, Drawer, Row, Space } from "antd";
import React, { Dispatch, SetStateAction, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { TestID, TestIDWrapper } from "../../shared/testids/testids";
import { ModalFeedback } from "../../modules/pages/feedback/ModalFeedback";
import { COREButton } from "../Action/COREButton";
import { COREDivider } from "../Layout/COREDivider";
import { COREMenu } from "../Navigation/COREMenu";
import { COREMenuItem } from "../Navigation/COREMenuItem";
import { COREIcon } from "../Content/COREIcon";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import "./COREUserDrawer.less";
import { COREBody } from "../Typography/COREBody";
import { HELP_URL, PRODUCT_GLOSSARY_URL, spacing } from "../../shared/global";
import { useFeatureFlags } from "../../shared/customHoooks/useFeatureFlags";
import { useTotalTodo } from "../../shared/customHoooks/useTasks";
import { purple100 } from "../Content/COREColour";
import { NotificationBadgeCount } from "../../modules/notifications/NotificationBadgeCount";
import { ThemeMenuItem } from "./ThemeMenuItem";

export type COREUserDrawerProps = {
  name: string;
  collapsed: boolean;
  setCollapsed: Dispatch<SetStateAction<boolean>>;
  testId: TestID;
};

export const getUserDrawerSelectedMenu = (pathname?: string) => {
  switch (pathname) {
    case "/account/user/profile":
      return "my-details";
    case "/account/user/preferences":
      return "preferences";
    case "/help":
      return "help";
    case "/todo":
      return "todo";
    case "/notifications":
      return "notifications";
    default:
      return "";
  }
};

export const COREUserDrawer: React.FC<COREUserDrawerProps> = ({
  name,
  collapsed = true,
  setCollapsed,
  testId,
}) => {
  const { total: totalTodo } = useTotalTodo();
  const onClose = () => {
    setCollapsed(true);
  };
  const { features } = useFeatureFlags();
  const navigate = useNavigate();
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] =
    useState<boolean>(false);

  const location = useLocation();
  const hasNotificationPermission = features?.includes("notifications");
  const hasTodoPermission = features?.includes("todo-list");
  const hasTransactionHistoryPermission = features?.includes(
    "account-my-company-transaction-history"
  );
  const hasHelpPermission = features?.includes("help");
  const hasThemePermission = features?.includes("themes");

  return (
    <Drawer
      title={
        <TestIDWrapper testID={(testId + "-title") as TestID}>
          <Row wrap={false} align={"middle"} justify={"space-between"}>
            <Col>
              <Space size={8}>
                <COREIcon
                  icon={icon({ name: "user", style: "regular" })}
                  size={"xs"}
                  className={"user-icon"}
                />
                <COREBody
                  marginBottom={false}
                  type={"p2"}
                  strong
                  testID={(testId + "-username") as TestID}
                >
                  {name}
                </COREBody>
              </Space>
            </Col>
            <Col>
              <COREButton
                testID={(testId + `-close-btn`) as TestID}
                onClick={onClose}
                type={"action"}
                size={"lg"}
                icon={
                  <COREIcon
                    icon={icon({ name: "circle-xmark", style: "solid" })}
                  />
                }
              />
            </Col>
          </Row>
        </TestIDWrapper>
      }
      placement="right"
      visible={!collapsed}
      closable={false}
      className={"core-user-drawer"}
      onClose={onClose}
    >
      <TestIDWrapper testID={(testId + "-body") as TestID}>
        <COREMenu
          className={"user-drawer-menu"}
          selectedKeys={[getUserDrawerSelectedMenu(location.pathname)]}
          testID={(testId + "-menu") as TestID}
        >
          <COREMenuItem
            key={"my-details"}
            icon={<COREIcon icon={icon({ name: "user", style: "regular" })} />}
            testID={(testId + "-menu-my-details") as TestID}
            to={"/account/user/profile"}
          >
            My Details
          </COREMenuItem>
          <COREMenuItem
            key={"preferences"}
            icon={<COREIcon icon={icon({ name: "gear", style: "regular" })} />}
            testID={(testId + "-menu-preference") as TestID}
            to={"/account/user/preferences"}
            disabled={!features.includes("account-my-details-preferences")}
          >
            Preferences
          </COREMenuItem>
          {hasThemePermission && (
            <ThemeMenuItem testID={(testId + "-menu-theme") as TestID} />
          )}
          {hasNotificationPermission && (
            <COREMenuItem
              badge={<NotificationBadgeCount />}
              key="notifications"
              icon={
                <COREIcon
                  icon={icon({
                    name: "bells",
                    style: "regular",
                  })}
                />
              }
              testID={(testId + "-notifications") as TestID}
              to={"/notifications"}
            >
              Notifications
            </COREMenuItem>
          )}
          {hasTodoPermission && (
            <COREMenuItem
              badge={
                <Badge
                  count={totalTodo}
                  style={{ backgroundColor: purple100 }}
                />
              }
              key="todo"
              icon={
                <COREIcon
                  icon={icon({
                    name: "clipboard-list-check",
                    style: "regular",
                  })}
                />
              }
              testID={(testId + "-todo") as TestID}
              to={"/todo"}
            >
              <span>To Do List</span>
            </COREMenuItem>
          )}
          {hasTransactionHistoryPermission && (
            <COREMenuItem
              key={"transactionHistory"}
              icon={
                <COREIcon
                  icon={icon({
                    name: "receipt",
                    style: "regular",
                  })}
                />
              }
              testID={(testId + "-transaction-history") as TestID}
              to={"/account/company/transaction-history"}
            >
              <span>Transaction history</span>
            </COREMenuItem>
          )}
          <COREMenuItem
            key={"project-watchlist"}
            icon={
              <COREIcon icon={icon({ name: "bookmark", style: "regular" })} />
            }
            testID={(testId + "-menu-project-watchlist") as TestID}
            style={{ display: `none` }}
          >
            Project Watchlist
          </COREMenuItem>
          {hasHelpPermission && (
            <>
              <COREMenuItem
                key={"help"}
                icon={
                  <COREIcon
                    icon={icon({ name: "circle-question", style: "regular" })}
                  />
                }
                testID={(testId + "-menu-help") as TestID}
                onClick={() =>
                  window.open(HELP_URL, "_blank", "noopener,noreferrer")
                }
              >
                Help / FAQ
              </COREMenuItem>
              <COREMenuItem
                key={"product-glossary"}
                icon={
                  <COREIcon
                    icon={icon({ name: "book-open", style: "regular" })}
                  />
                }
                testID={(testId + "-menu-product-glossary") as TestID}
                onClick={() =>
                  window.open(
                    PRODUCT_GLOSSARY_URL,
                    "_blank",
                    "noopener,noreferrer"
                  )
                }
              >
                Product Glossary
              </COREMenuItem>
              <COREMenuItem
                key={"support"}
                icon={
                  <COREIcon
                    icon={icon({ name: "envelope", style: "regular" })}
                  />
                }
                testID={(testId + "-menu-support") as TestID}
                onClick={() => setIsFeedbackModalOpen(true)}
              >
                Support
              </COREMenuItem>
            </>
          )}
        </COREMenu>
        <Row gutter={[0, spacing.md]} className={"bottom-container"}>
          <Col span={24}>
            <div className={"divider"}>
              <COREDivider space={"sm"} />
            </div>
          </Col>
          <Col span={24}>
            <COREButton
              size={"lg"}
              danger
              icon={
                <COREIcon
                  icon={icon({
                    name: "right-from-bracket",
                    style: "regular",
                  })}
                />
              }
              onClick={() => {
                navigate(`/logout`);
              }}
            >
              Log out
            </COREButton>
          </Col>
        </Row>
      </TestIDWrapper>
      <ModalFeedback
        isFeedbackModalOpen={isFeedbackModalOpen}
        setIsFeedbackModalOpen={setIsFeedbackModalOpen}
      />
    </Drawer>
  );
};
